<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500"
                                        @click="goBack"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Offering</h1>
                                </div>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading offering...</span>
                </div>

                <!-- Main Content -->
                <main v-else class="p-6">
                        <!-- Form Content -->
                        <div class="grid gap-6 grid-cols-1 md:grid-cols-2">
                                <!-- Basic Properties Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                                                <div class="flex items-center gap-3">
                                                        <h2 class="text-base font-semibold text-gray-900 m-0">Basic Properties</h2>
                                                        <span v-if="hasUnsavedBasic" class="text-xs text-orange-7 font-medium">Unsaved changes</span>
                                                </div>
                                                <button
                                                        type="button"
                                                        :disabled="isSavingBasic || !hasUnsavedBasic"
                                                        class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-orange-7 text-white rounded-md text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="saveBasicProperties"
                                                >
                                                        <svg v-if="isSavingBasic" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        Save
                                                </button>
                                        </div>
                                        <div class="p-6 flex flex-col gap-5">
                                                <!-- Offering ID -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Offering ID</label>
                                                        <input
                                                                type="text"
                                                                :value="formData.id"
                                                                disabled
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500 box-border"
                                                        >
                                                </div>
                                                <!-- Name -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
                                                        <input
                                                                v-model="formData.name"
                                                                type="text"
                                                                placeholder="Enter offering name"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <!-- External ID -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">External ID</label>
                                                        <input
                                                                v-model="formData.externalid"
                                                                type="text"
                                                                placeholder="External production ID"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <!-- In Stock Toggle -->
                                                <div class="flex items-center justify-between">
                                                        <label class="text-sm font-medium text-gray-700">In Stock</label>
                                                        <button
                                                                type="button"
                                                                class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
                                                                :class="formData.instock ? 'bg-orange-7' : 'bg-gray-300'"
                                                                @click="formData.instock = formData.instock ? 0 : 1"
                                                        >
                                                                <span
                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm"
                                                                        :class="formData.instock ? 'left-[1.375rem]' : 'left-0.5'"
                                                                ></span>
                                                        </button>
                                                </div>
                                        </div>
                                </div>

                                <!-- Image Settings Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                                                <div class="flex items-center gap-3">
                                                        <h2 class="text-base font-semibold text-gray-900 m-0">Images</h2>
                                                        <span v-if="hasUnsavedImages" class="text-xs text-orange-7 font-medium">Unsaved changes</span>
                                                </div>
                                                <button
                                                        type="button"
                                                        :disabled="isSavingImages || !hasUnsavedImages"
                                                        class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-orange-7 text-white rounded-md text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="saveImages"
                                                >
                                                        <svg v-if="isSavingImages" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        Save
                                                </button>
                                        </div>
                                        <div class="p-6 flex flex-col gap-5">
                                                <!-- Thumbnail -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Thumbnail</label>
                                                        <div class="flex gap-3 items-center">
                                                                <div v-if="formData.thumbnail" class="relative">
                                                                        <img
                                                                                :src="formData.thumbnail"
                                                                                alt="Thumbnail"
                                                                                class="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                                                        >
                                                                </div>
                                                                <div v-else class="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                                                                        <svg class="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                                                                        </svg>
                                                                </div>
                                                                <div class="flex flex-col gap-2">
                                                                        <button
                                                                                v-if="hasFilestackClient"
                                                                                type="button"
                                                                                :disabled="isUploading.thumbnail"
                                                                                class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-orange-7 text-white rounded-md text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
                                                                                @click="openFilePicker('thumbnail')"
                                                                        >
                                                                                <svg v-if="isUploading.thumbnail" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                                <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                                                </svg>
                                                                                {{ formData.thumbnail ? 'Change' : 'Upload' }}
                                                                        </button>
                                                                        <button
                                                                                v-if="formData.thumbnail"
                                                                                type="button"
                                                                                class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-white text-red-600 border border-red-300 rounded-md text-sm font-medium cursor-pointer transition-colors hover:bg-red-50"
                                                                                @click="removeImage('thumbnail')"
                                                                        >
                                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                </svg>
                                                                                Remove
                                                                        </button>
                                                                </div>
                                                        </div>
                                                </div>
                                                <!-- Mask -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Mask</label>
                                                        <div class="flex gap-3 items-center">
                                                                <div v-if="formData.mask" class="relative">
                                                                        <img
                                                                                :src="formData.mask"
                                                                                alt="Mask"
                                                                                class="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                                                        >
                                                                </div>
                                                                <div v-else class="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                                                                        <svg class="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                                                                        </svg>
                                                                </div>
                                                                <div class="flex flex-col gap-2">
                                                                        <button
                                                                                v-if="hasFilestackClient"
                                                                                type="button"
                                                                                :disabled="isUploading.mask"
                                                                                class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-orange-7 text-white rounded-md text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
                                                                                @click="openFilePicker('mask')"
                                                                        >
                                                                                <svg v-if="isUploading.mask" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                                <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                                                </svg>
                                                                                {{ formData.mask ? 'Change' : 'Upload' }}
                                                                        </button>
                                                                        <button
                                                                                v-if="formData.mask"
                                                                                type="button"
                                                                                class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-white text-red-600 border border-red-300 rounded-md text-sm font-medium cursor-pointer transition-colors hover:bg-red-50"
                                                                                @click="removeImage('mask')"
                                                                        >
                                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                </svg>
                                                                                Remove
                                                                        </button>
                                                                </div>
                                                        </div>
                                                </div>
                                                <!-- Overlay -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Overlay</label>
                                                        <div class="flex gap-3 items-center">
                                                                <div v-if="formData.overlay" class="relative">
                                                                        <img
                                                                                :src="formData.overlay"
                                                                                alt="Overlay"
                                                                                class="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                                                        >
                                                                </div>
                                                                <div v-else class="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                                                                        <svg class="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                                                                        </svg>
                                                                </div>
                                                                <div class="flex flex-col gap-2">
                                                                        <button
                                                                                v-if="hasFilestackClient"
                                                                                type="button"
                                                                                :disabled="isUploading.overlay"
                                                                                class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-orange-7 text-white rounded-md text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
                                                                                @click="openFilePicker('overlay')"
                                                                        >
                                                                                <svg v-if="isUploading.overlay" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                                <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                                                </svg>
                                                                                {{ formData.overlay ? 'Change' : 'Upload' }}
                                                                        </button>
                                                                        <button
                                                                                v-if="formData.overlay"
                                                                                type="button"
                                                                                class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-white text-red-600 border border-red-300 rounded-md text-sm font-medium cursor-pointer transition-colors hover:bg-red-50"
                                                                                @click="removeImage('overlay')"
                                                                        >
                                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                </svg>
                                                                                Remove
                                                                        </button>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                <!-- Enhancement Settings Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                                                <div class="flex items-center gap-3">
                                                        <h2 class="text-base font-semibold text-gray-900 m-0">Enhancement Settings</h2>
                                                        <span v-if="hasUnsavedEnhancement" class="text-xs text-orange-7 font-medium">Unsaved changes</span>
                                                </div>
                                                <button
                                                        type="button"
                                                        :disabled="isSavingEnhancement || !hasUnsavedEnhancement"
                                                        class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-orange-7 text-white rounded-md text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="saveEnhancementSettings"
                                                >
                                                        <svg v-if="isSavingEnhancement" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        Save
                                                </button>
                                        </div>
                                        <div class="p-6 flex flex-col gap-4">
                                                <!-- Apply Upscaling -->
                                                <div class="flex items-center justify-between">
                                                        <div>
                                                                <label class="text-sm font-medium text-gray-700 block">Apply Upscaling</label>
                                                                <span class="text-xs text-gray-500">Enhance image resolution</span>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
                                                                :class="formData.applyUpscaling ? 'bg-orange-7' : 'bg-gray-300'"
                                                                @click="formData.applyUpscaling = formData.applyUpscaling ? 0 : 1"
                                                        >
                                                                <span
                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm"
                                                                        :class="formData.applyUpscaling ? 'left-[1.375rem]' : 'left-0.5'"
                                                                ></span>
                                                        </button>
                                                </div>
                                                <!-- Apply Enhancement -->
                                                <div class="flex items-center justify-between">
                                                        <div>
                                                                <label class="text-sm font-medium text-gray-700 block">Apply Enhancement</label>
                                                                <span class="text-xs text-gray-500">Apply AI image enhancement</span>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
                                                                :class="formData.applyEnhancement ? 'bg-orange-7' : 'bg-gray-300'"
                                                                @click="formData.applyEnhancement = formData.applyEnhancement ? 0 : 1"
                                                        >
                                                                <span
                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm"
                                                                        :class="formData.applyEnhancement ? 'left-[1.375rem]' : 'left-0.5'"
                                                                ></span>
                                                        </button>
                                                </div>
                                        </div>
                                </div>

                                <!-- Reference IDs Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                                                <div class="flex items-center gap-3">
                                                        <h2 class="text-base font-semibold text-gray-900 m-0">References</h2>
                                                        <span v-if="hasUnsavedReferences" class="text-xs text-orange-7 font-medium">Unsaved changes</span>
                                                </div>
                                                <button
                                                        type="button"
                                                        :disabled="isSavingReferences || !hasUnsavedReferences"
                                                        class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-orange-7 text-white rounded-md text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="saveReferences"
                                                >
                                                        <svg v-if="isSavingReferences" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        Save
                                                </button>
                                        </div>
                                        <div class="p-6 flex flex-col gap-5">
                                                <!-- PDP -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">PDP</label>
                                                        <select
                                                                v-model="formData.pdpid"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border bg-white focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                                <option :value="null">-- None --</option>
                                                                <option v-for="pdp in pdps" :key="pdp.id" :value="pdp.id">
                                                                        {{ pdp.name || pdp.id }}
                                                                </option>
                                                        </select>
                                                </div>
                                                <!-- Model 2D -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Model 2D</label>
                                                        <select
                                                                v-model="formData.model2did"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border bg-white focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                                <option :value="null">-- None --</option>
                                                                <option v-for="model in model2ds" :key="model.id" :value="model.id">
                                                                        {{ model.name || model.id }}
                                                                </option>
                                                        </select>
                                                </div>
                                                <!-- Model 3D -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Model 3D</label>
                                                        <select
                                                                v-model="formData.model3did"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border bg-white focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                                <option :value="null">-- None --</option>
                                                                <option v-for="model in model3ds" :key="model.id" :value="model.id">
                                                                        {{ model.name || model.id }}
                                                                </option>
                                                        </select>
                                                </div>
                                                <!-- Theme -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Theme</label>
                                                        <select
                                                                v-model="formData.themeid"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border bg-white focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                                <option :value="null">-- None --</option>
                                                                <option v-for="theme in themes" :key="theme.id" :value="theme.id">
                                                                        {{ theme.name || theme.id }}
                                                                </option>
                                                        </select>
                                                </div>
                                                <!-- Flex Group -->
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1.5">Flex Group</label>
                                                        <select
                                                                v-model="formData.flexgroupid"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border bg-white focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                                <option :value="null">-- None --</option>
                                                                <option v-for="group in flexGroups" :key="group.id" :value="group.id">
                                                                        {{ group.name || group.id }}
                                                                </option>
                                                        </select>
                                                </div>
                                        </div>
                                </div>
                        </div>

                        <!-- Linked Options Card (Full Width) -->
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                        <h2 class="text-base font-semibold text-gray-900 m-0">Linked Offering Options</h2>
                                </div>
                                <div class="p-6">
                                        <!-- Loading State -->
                                        <div v-if="isLoadingOptions" class="flex items-center gap-3 text-gray-500">
                                                <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span class="text-sm">Loading linked options...</span>
                                        </div>

                                        <!-- No Options -->
                                        <div v-else-if="linkedOptions.length === 0" class="text-center py-8">
                                                <svg class="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 6h.008v.008H6V6z" />
                                                </svg>
                                                <p class="text-gray-500 text-sm m-0">No offering options are linked to this offering.</p>
                                        </div>

                                        <!-- Options List -->
                                        <div v-else>
                                                <div v-if="isSavingOrder" class="flex items-center gap-2 mb-3 text-sm text-gray-500">
                                                        <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Saving order...
                                                </div>
                                                <draggable
                                                        v-model="linkedOptions"
                                                        item-key="id"
                                                        handle=".drag-handle"
                                                        ghost-class="opacity-50"
                                                        class="space-y-4"
                                                        @end="onReorderOptions"
                                                >
                                                        <template #item="{ element: option }">
                                                                <div class="border border-gray-200 rounded-lg overflow-hidden">
                                                                        <div class="py-3 px-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                                                                                <div class="flex items-center gap-3">
                                                                                        <div class="drag-handle cursor-grab text-gray-400 hover:text-gray-600">
                                                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 8h16M4 16h16" />
                                                                                                </svg>
                                                                                        </div>
                                                                                        <span class="text-sm font-medium text-gray-900">{{ option.name }}</span>
                                                                                        <span class="text-xs text-gray-500">(ID: {{ option.id }})</span>
                                                                                </div>
                                                                                <router-link
                                                                                        :to="`/offeringoption/${option.id}`"
                                                                                        class="text-sm text-orange-7 font-medium no-underline hover:text-orange-6"
                                                                                >
                                                                                        View Option
                                                                                </router-link>
                                                                        </div>
                                                                        <div class="p-4">
                                                                                <div v-if="option.values && option.values.length > 0" class="flex flex-wrap gap-2">
                                                                                        <span
                                                                                                v-for="val in option.values"
                                                                                                :key="val.id"
                                                                                                class="inline-flex items-center gap-2 py-1.5 px-3 bg-gray-100 rounded-full text-sm"
                                                                                        >
                                                                                                <img
                                                                                                        v-if="val.image"
                                                                                                        :src="val.image"
                                                                                                        :alt="val.value"
                                                                                                        class="w-5 h-5 rounded-full object-cover"
                                                                                                >
                                                                                                <span class="text-gray-700">{{ val.value }}</span>
                                                                                        </span>
                                                                                </div>
                                                                                <p v-else class="text-sm text-gray-500 m-0">No values linked</p>
                                                                        </div>
                                                                </div>
                                                        </template>
                                                </draggable>
                                        </div>
                                </div>
                        </div>

                        </main>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                class="flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                                <button
                                        type="button"
                                        class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer hover:opacity-100"
                                        @click="hideToast"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, getCurrentInstance, Ref, ComputedRef, ComponentInternalInstance } from 'vue';
import { useRoute, useRouter, Router, RouteLocationNormalizedLoaded } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import OfferingService from 'services/OfferingService';
import PDPService from 'services/PDPService';
import Model2DService from 'services/Model2DService';
import Model3DService from 'services/Model3DService';
import ThemeService from 'services/ThemeService';
import FlexGroupService from 'services/FlexGroupService';
import OfferingOptionValueService from 'services/OfferingOptionValueService';
import OfferingOptionService from 'services/OfferingOptionService';
import OfferingOfferingOptionValueService from 'services/OfferingOfferingOptionValueService';
import draggable from 'vuedraggable';
import type { PDPModel, Model2DModel, Model3DModel, ThemeModel, FlexGroupModel, OfferingOptionValueModel } from '@sosocio/creator/interfaces';

interface FormData {
        id: number | null;
        name: string;
        externalid: string;
        instock: number;
        thumbnail: string;
        mask: string;
        overlay: string;
        applyUpscaling: number;
        applyEnhancement: number;
        pdpid: number | null;
        model2did: number | null;
        model3did: number | null;
        themeid: number | null;
        flexgroupid: number | null;
}

interface LinkedOption {
        id: number;
        name: string;
        serialnumber?: number;
        values?: OfferingOptionValueModel[];
}

interface IsUploading {
        thumbnail: boolean;
        mask: boolean;
        overlay: boolean;
}

interface FilestackUploadedFile {
        url: string;
        filename?: string;
        mimetype?: string;
        size?: number;
}

interface FilestackUploadResult {
        filesUploaded: FilestackUploadedFile[];
        filesFailed?: FilestackUploadedFile[];
}

interface FilestackPickerOptions {
        accept?: string | string[];
        maxFiles?: number;
        onUploadDone?: (result: FilestackUploadResult) => void;
        onClose?: () => void;
}

interface FilestackPicker {
        open: () => void;
}

interface FilestackClient {
        picker: (options: FilestackPickerOptions) => FilestackPicker;
}

interface JunctionRecord {
        offeringoptionvalueid: number;
}

type ImageField = 'thumbnail' | 'mask' | 'overlay';

export default defineComponent({
        name: 'EditOfferingPage',
        components: { draggable },
        setup() {
                const route: RouteLocationNormalizedLoaded = useRoute();
                const router: Router = useRouter();
                const auth0 = useAuth0();
                const instance: ComponentInternalInstance | null = getCurrentInstance();
                const filestackClient: FilestackClient | undefined = instance?.appContext.config.globalProperties.$client;

                const isLoading: Ref<boolean> = ref(true);
                const isSavingBasic: Ref<boolean> = ref(false);
                const isSavingImages: Ref<boolean> = ref(false);
                const isSavingEnhancement: Ref<boolean> = ref(false);
                const isSavingReferences: Ref<boolean> = ref(false);
                const isUploading: Ref<IsUploading> = ref({ thumbnail: false, mask: false, overlay: false });
                const formData: Ref<FormData> = ref({
                        id: null,
                        name: '',
                        externalid: '',
                        instock: 1,
                        thumbnail: '',
                        mask: '',
                        overlay: '',
                        applyUpscaling: 0,
                        applyEnhancement: 0,
                        pdpid: null,
                        model2did: null,
                        model3did: null,
                        themeid: null,
                        flexgroupid: null,
                });
                const originalData: Ref<Partial<FormData>> = ref({});

                const pdps: Ref<PDPModel[]> = ref([]);
                const model2ds: Ref<Model2DModel[]> = ref([]);
                const model3ds: Ref<Model3DModel[]> = ref([]);
                const themes: Ref<ThemeModel[]> = ref([]);
                const flexGroups: Ref<FlexGroupModel[]> = ref([]);
                const linkedOptions: Ref<LinkedOption[]> = ref([]);
                const isLoadingOptions: Ref<boolean> = ref(false);
                const isSavingOrder: Ref<boolean> = ref(false);

                const toastMessage: Ref<string> = ref('');
                const toastVariant: Ref<string> = ref('success');
                let toastTimeout: ReturnType<typeof setTimeout> | null = null;

                const offeringId: ComputedRef<number> = computed(() => Number(route.params.id));
                const loggedIn: ComputedRef<boolean> = computed(() => auth0.isAuthenticated.value);
                const hasFilestackClient: ComputedRef<boolean> = computed(() => !!filestackClient);

                const hasUnsavedBasic: ComputedRef<boolean> = computed(() => {
                        return formData.value.name !== originalData.value.name ||
                                formData.value.externalid !== originalData.value.externalid ||
                                formData.value.instock !== originalData.value.instock;
                });

                const hasUnsavedImages: ComputedRef<boolean> = computed(() => {
                        return formData.value.thumbnail !== originalData.value.thumbnail ||
                                formData.value.mask !== originalData.value.mask ||
                                formData.value.overlay !== originalData.value.overlay;
                });

                const hasUnsavedEnhancement: ComputedRef<boolean> = computed(() => {
                        return formData.value.applyUpscaling !== originalData.value.applyUpscaling ||
                                formData.value.applyEnhancement !== originalData.value.applyEnhancement;
                });

                const hasUnsavedReferences: ComputedRef<boolean> = computed(() => {
                        return formData.value.pdpid !== originalData.value.pdpid ||
                                formData.value.model2did !== originalData.value.model2did ||
                                formData.value.model3did !== originalData.value.model3did ||
                                formData.value.themeid !== originalData.value.themeid ||
                                formData.value.flexgroupid !== originalData.value.flexgroupid;
                });

                const showToast = (message: string, variant: string = 'success'): void => {
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 4000);
                };

                const hideToast = (): void => {
                        toastMessage.value = '';
                        if (toastTimeout) clearTimeout(toastTimeout);
                };

                const loadReferenceData = async (): Promise<void> => {
                        const params = new URLSearchParams({ limit: '0' });
                        try {
                                const [pdpData, model2dData, model3dData, themeData, flexGroupData] = await Promise.all([
                                        PDPService.getAll(params),
                                        Model2DService.getAll(params),
                                        Model3DService.getAll(params),
                                        ThemeService.getAll(params),
                                        FlexGroupService.getAll(params),
                                ]);
                                pdps.value = pdpData;
                                model2ds.value = model2dData;
                                model3ds.value = model3dData;
                                themes.value = themeData;
                                flexGroups.value = flexGroupData;
                        } catch (error: unknown) {
                                console.error('Failed to load reference data:', error);
                        }
                };

                const loadLinkedOptions = async (): Promise<void> => {
                        isLoadingOptions.value = true;
                        try {
                                const junctionRecords: JunctionRecord[] = await OfferingOfferingOptionValueService.getByOffering(offeringId.value);
                                
                                if (junctionRecords.length === 0) {
                                        linkedOptions.value = [];
                                        return;
                                }

                                const valueIds = [...new Set(junctionRecords.map((r: JunctionRecord) => r.offeringoptionvalueid))].filter(Boolean) as number[];
                                
                                if (valueIds.length === 0) {
                                        linkedOptions.value = [];
                                        return;
                                }

                                const optionValues: OfferingOptionValueModel[] = await OfferingOptionValueService.getByIds(valueIds);

                                const optionIds = [...new Set(optionValues.map((v: OfferingOptionValueModel) => v.offeringoptionid))].filter(Boolean) as number[];

                                if (optionIds.length === 0) {
                                        linkedOptions.value = [];
                                        return;
                                }

                                const options = await OfferingOptionService.getByIds(optionIds) as LinkedOption[];

                                linkedOptions.value = options
                                        .map((option: LinkedOption) => ({
                                                ...option,
                                                values: optionValues.filter((v: OfferingOptionValueModel) => v.offeringoptionid === option.id)
                                        }))
                                        .sort((a: LinkedOption, b: LinkedOption) => (a.serialnumber || 0) - (b.serialnumber || 0));
                        } catch (error: unknown) {
                                console.error('Failed to load linked options:', error);
                                showToast('Failed to load linked options', 'error');
                        } finally {
                                isLoadingOptions.value = false;
                        }
                };

                const loadData = async (): Promise<void> => {
                        if (!loggedIn.value) return;

                        isLoading.value = true;
                        try {
                                await loadReferenceData();
                                const data = await OfferingService.get(offeringId.value);
                                formData.value = {
                                        id: data.id ?? null,
                                        name: data.name ?? '',
                                        externalid: data.externalid ?? '',
                                        instock: data.instock ?? 1,
                                        thumbnail: data.thumbnail ?? '',
                                        mask: data.mask ?? '',
                                        overlay: data.overlay ?? '',
                                        applyUpscaling: data.applyUpscaling ?? 0,
                                        applyEnhancement: data.applyEnhancement ?? 0,
                                        pdpid: data.pdpid ?? null,
                                        model2did: data.model2did ?? null,
                                        model3did: data.model3did ?? null,
                                        themeid: data.themeid ?? null,
                                        flexgroupid: data.flexgroupid ?? null,
                                };
                                originalData.value = { ...formData.value };
                                loadLinkedOptions();
                        } catch (error: unknown) {
                                const message = error instanceof Error ? error.message : 'Failed to load offering';
                                showToast(message, 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };

                const saveBasicProperties = async (): Promise<void> => {
                        if (!loggedIn.value || isSavingBasic.value) return;
                        isSavingBasic.value = true;
                        try {
                                const payload = {
                                        name: formData.value.name,
                                        externalid: formData.value.externalid,
                                        instock: formData.value.instock,
                                };
                                await OfferingService.update(offeringId.value, payload);
                                originalData.value.name = formData.value.name;
                                originalData.value.externalid = formData.value.externalid;
                                originalData.value.instock = formData.value.instock;
                                showToast('Basic properties saved', 'success');
                        } catch (error: unknown) {
                                const message = error instanceof Error ? error.message : 'Failed to save';
                                showToast(message, 'error');
                        } finally {
                                isSavingBasic.value = false;
                        }
                };

                const saveImages = async (): Promise<void> => {
                        if (!loggedIn.value || isSavingImages.value) return;
                        isSavingImages.value = true;
                        try {
                                const payload = {
                                        thumbnail: formData.value.thumbnail,
                                        mask: formData.value.mask,
                                        overlay: formData.value.overlay,
                                };
                                await OfferingService.update(offeringId.value, payload);
                                originalData.value.thumbnail = formData.value.thumbnail;
                                originalData.value.mask = formData.value.mask;
                                originalData.value.overlay = formData.value.overlay;
                                showToast('Images saved', 'success');
                        } catch (error: unknown) {
                                const message = error instanceof Error ? error.message : 'Failed to save';
                                showToast(message, 'error');
                        } finally {
                                isSavingImages.value = false;
                        }
                };

                const saveEnhancementSettings = async (): Promise<void> => {
                        if (!loggedIn.value || isSavingEnhancement.value) return;
                        isSavingEnhancement.value = true;
                        try {
                                const payload = {
                                        applyUpscaling: formData.value.applyUpscaling,
                                        applyEnhancement: formData.value.applyEnhancement,
                                };
                                await OfferingService.update(offeringId.value, payload);
                                originalData.value.applyUpscaling = formData.value.applyUpscaling;
                                originalData.value.applyEnhancement = formData.value.applyEnhancement;
                                showToast('Enhancement settings saved', 'success');
                        } catch (error: unknown) {
                                const message = error instanceof Error ? error.message : 'Failed to save';
                                showToast(message, 'error');
                        } finally {
                                isSavingEnhancement.value = false;
                        }
                };

                const saveReferences = async (): Promise<void> => {
                        if (!loggedIn.value || isSavingReferences.value) return;
                        isSavingReferences.value = true;
                        try {
                                const payload = {
                                        pdpid: formData.value.pdpid,
                                        model2did: formData.value.model2did,
                                        model3did: formData.value.model3did,
                                        themeid: formData.value.themeid,
                                        flexgroupid: formData.value.flexgroupid,
                                };
                                await OfferingService.update(offeringId.value, payload);
                                originalData.value.pdpid = formData.value.pdpid;
                                originalData.value.model2did = formData.value.model2did;
                                originalData.value.model3did = formData.value.model3did;
                                originalData.value.themeid = formData.value.themeid;
                                originalData.value.flexgroupid = formData.value.flexgroupid;
                                showToast('References saved', 'success');
                        } catch (error: unknown) {
                                const message = error instanceof Error ? error.message : 'Failed to save';
                                showToast(message, 'error');
                        } finally {
                                isSavingReferences.value = false;
                        }
                };

                const goBack = (): void => {
                        router.push('/offerings');
                };

                const openFilePicker = (field: ImageField): void => {
                        if (!filestackClient) {
                                showToast('File upload is not available', 'error');
                                return;
                        }
                        isUploading.value[field] = true;
                        const options: FilestackPickerOptions = {
                                accept: 'image/*',
                                maxFiles: 1,
                                onUploadDone: (result: FilestackUploadResult): void => {
                                        if (result.filesUploaded && result.filesUploaded.length > 0) {
                                                formData.value[field] = result.filesUploaded[0].url;
                                        }
                                        isUploading.value[field] = false;
                                },
                                onClose: (): void => {
                                        isUploading.value[field] = false;
                                },
                        };
                        filestackClient.picker(options).open();
                };

                const removeImage = (field: ImageField): void => {
                        formData.value[field] = '';
                };

                const onReorderOptions = async (): Promise<void> => {
                        const hasChanged = linkedOptions.value.some((option: LinkedOption, index: number) => {
                                const expectedSerial = index + 1;
                                return (option.serialnumber || 0) !== expectedSerial;
                        });

                        if (!hasChanged) {
                                return;
                        }

                        const previousOrder = linkedOptions.value.map((opt: LinkedOption) => ({ ...opt }));
                        isSavingOrder.value = true;
                        
                        try {
                                const updates = linkedOptions.value.map((option: LinkedOption, index: number) => ({
                                        ...option,
                                        serialnumber: index + 1
                                }));
                                linkedOptions.value = updates;

                                await Promise.all(
                                        updates.map((option: LinkedOption) => 
                                                OfferingOptionService.update(option.id, { serialnumber: option.serialnumber })
                                        )
                                );

                                showToast('Option order saved', 'success');
                        } catch (error: unknown) {
                                console.error('Failed to save order:', error);
                                linkedOptions.value = previousOrder;
                                showToast('Failed to save order. Changes reverted.', 'error');
                        } finally {
                                isSavingOrder.value = false;
                        }
                };

                watch(loggedIn, (isLoggedIn: boolean): void => {
                        if (isLoggedIn) {
                                loadData();
                        }
                }, { immediate: true });

                return {
                        isLoading,
                        isUploading,
                        formData,
                        pdps,
                        model2ds,
                        model3ds,
                        themes,
                        flexGroups,
                        linkedOptions,
                        isLoadingOptions,
                        isSavingOrder,
                        onReorderOptions,
                        toastMessage,
                        toastVariant,
                        hasFilestackClient,
                        // Section-specific save
                        isSavingBasic,
                        isSavingImages,
                        isSavingEnhancement,
                        isSavingReferences,
                        hasUnsavedBasic,
                        hasUnsavedImages,
                        hasUnsavedEnhancement,
                        hasUnsavedReferences,
                        saveBasicProperties,
                        saveImages,
                        saveEnhancementSettings,
                        saveReferences,
                        goBack,
                        openFilePicker,
                        removeImage,
                        hideToast,
                };
        },
});
</script>
